/**
 * Journey id — one anonymous id per page load, linking this visit's API
 * calls, consent records and orders in Application Insights (see
 * docs/observability.md).
 *
 * Kept in memory only: nothing is written to cookies or storage, so it needs
 * no cookie consent. The trade-off is that a full reload or the Stripe
 * redirect starts a new id — orders carry the id they were created with, so
 * the path after checkout is still linked through the order.
 */

const newId = (): string => {
	// crypto.randomUUID needs a secure context; plain-http LAN dev doesn't have one.
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const journeyId = newId();

export const JOURNEY_HEADER = 'x-journey-id';

/**
 * Add the journey header to every fetch() aimed at our API. Patched once at
 * startup because API calls use bare fetch() across many components. Other
 * origins are left untouched (an unexpected header would trigger CORS
 * preflights on third-party calls).
 */
export const installJourneyHeader = (apiBaseUrl: string | undefined): void => {
	if (!apiBaseUrl || typeof window === 'undefined' || !window.fetch) return;
	let apiOrigin: string;
	try {
		apiOrigin = new URL(apiBaseUrl, window.location.href).origin;
	} catch {
		return;
	}

	const originalFetch = window.fetch.bind(window);
	window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
		const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
		let targetOrigin: string | null = null;
		try {
			targetOrigin = new URL(url, window.location.href).origin;
		} catch {
			// unparsable URL — let fetch report it
		}
		if (targetOrigin !== apiOrigin) return originalFetch(input, init);

		const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
		if (!headers.has(JOURNEY_HEADER)) headers.set(JOURNEY_HEADER, journeyId);
		return originalFetch(input, { ...init, headers });
	};
};
